/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { IconButton, Typography2 } from '@coursera/cds-core';
import {
  AIGenerateBrandedNewIcon,
  ExternalLinkIcon,
  MaximizeIcon,
  MinimizeIcon,
  SearchIcon,
  SwapIcon,
} from '@coursera/cds-icons';

import _t from 'i18n!nls/cds-labs';

export type ChatHeaderVariant = 'default' | 'splitSheet' | 'collapsed';

export type ChatPopupButtonMode = 'in' | 'out';

export type ChatHeaderProps = {
  /**
   * Visual variant controlling layout and presence of handle/fade. Defaults to 'default'.
   * Note: when 'collapsed', the header is aria-hidden and non-interactive — the
   * parent component is responsible for providing an accessible expand affordance.
   */
  variant?: ChatHeaderVariant;
  /** Optional chat session name displayed next to the AI icon. */
  name?: string;
  /** Called when "Swap position" is clicked. Omit to hide the button. */
  onSwapPosition?: () => void;
  /** Controls which pop button variant is shown: 'in' to pop into page, 'out' to pop out to popup. Omit to hide the button. */
  popupButtonMode?: ChatPopupButtonMode;
  /** Called when the pop in/out button is clicked. */
  onPopupButtonClick?: () => void;
  /** Called when "Find in chat" is clicked. Omit to hide the button. */
  onSearch?: () => void;
  /** Ref forwarded to the search button element. */
  searchButtonRef?: React.RefObject<HTMLButtonElement>;
  /** Called when "New chat" is clicked. Omit to hide the button. */
  onNewChat?: () => void;
  /** Called when "Settings" is clicked. Omit to hide the button. */
  onSettings?: () => void;
  /** Ref forwarded to the settings button element. */
  settingsButtonRef?: React.RefObject<HTMLButtonElement>;
  /** Whether the chat is currently in fullscreen mode (controls icon state). */
  isFullScreen?: boolean;
  /** Called when the fullscreen toggle is clicked. Omit to hide the button. */
  onToggleFullScreen?: () => void;
  /** Called when "Close" is clicked. Omit to hide the button. */
  onClose?: () => void;
  /** When true, all action buttons are disabled. */
  loading?: boolean;
  /**
   * Render a gradient fade below the header to soften content scrolling beneath it. Defaults to false.
   * Note: The fade extends below the header boundary via a negative bottom offset — the parent
   * container must not clip overflow (avoid `overflow: hidden`).
   */
  showFade?: boolean;
};

const styles = {
  root: css`
    position: relative;
    z-index: 1;
    border-top-left-radius: var(--cds-border-radius-200);
    border-top-right-radius: var(--cds-border-radius-200);
    background: var(--cds-color-neutral-background-primary);
  `,
  handleWrapper: css`
    display: flex;
    justify-content: center;
    padding-top: var(--cds-spacing-100);
    padding-bottom: var(--cds-spacing-50);
  `,
  handleBar: css`
    width: var(--cds-spacing-400);
    height: var(--cds-spacing-50);
    border-radius: var(--cds-border-radius-max);
    background: var(--cds-color-neutral-stroke-primary-weak);
  `,
  headerRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--cds-spacing-100) var(--cds-spacing-150);
  `,
  titleSection: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    flex-shrink: 0;
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    flex-shrink: 0;
  `,
  fade: css`
    position: absolute;
    bottom: calc(-1 * var(--cds-spacing-400));
    left: 0;
    right: 0;
    height: var(--cds-spacing-400);
    pointer-events: none;
    background: linear-gradient(to bottom, var(--cds-color-neutral-background-primary), transparent);
  `,
  swapIconRotated: css`
    transform: rotate(90deg);
  `,
};

const ChatHeader = ({
  variant = 'default',
  name,
  onSwapPosition,
  popupButtonMode,
  onPopupButtonClick,
  onSearch,
  searchButtonRef,
  onNewChat,
  onSettings,
  settingsButtonRef,
  isFullScreen = false,
  onToggleFullScreen,
  onClose,
  loading = false,
  showFade = false,
}: ChatHeaderProps) => {
  const isCollapsed = variant === 'collapsed';
  const showHandle = variant === 'collapsed';

  const hasActions =
    onSwapPosition || popupButtonMode || onSearch || onNewChat || onSettings || onToggleFullScreen || onClose;

  return (
    <header
      css={styles.root}
      aria-label={isCollapsed ? undefined : _t('Coursera AI Chat')}
      aria-hidden={isCollapsed ? true : undefined}
      data-testid="chat-header"
    >
      {showHandle && (
        <div css={styles.handleWrapper} aria-hidden={true}>
          <div css={styles.handleBar} />
        </div>
      )}
      {!isCollapsed && (
        <>
          <div css={styles.headerRow}>
            <div css={styles.titleSection}>
              <AIGenerateBrandedNewIcon size="large" aria-hidden={true} />
              {name && (
                <Typography2 component="span" variant="subtitleMedium">
                  {name}
                </Typography2>
              )}
            </div>
            {hasActions && (
              <div css={styles.actions}>
                {onSwapPosition && (
                  <IconButton
                    variant="ghostSecondary"
                    size="small"
                    icon={<SwapIcon css={styles.swapIconRotated} />}
                    aria-label={_t('Swap the popup position between left and right')}
                    tooltip={_t('Swap the popup position between left and right')}
                    tooltipPlacement="bottom"
                    onClick={onSwapPosition}
                    disabled={loading}
                    data-testid="chat-header-swap-position"
                  />
                )}
                {popupButtonMode && (
                  <IconButton
                    variant="ghostSecondary"
                    size="small"
                    icon={<ExternalLinkIcon />}
                    aria-label={
                      popupButtonMode === 'in'
                        ? _t('Pop-in the chat into the page')
                        : _t('Pop-out the chat into a popup')
                    }
                    tooltip={
                      popupButtonMode === 'in'
                        ? _t('Pop-in the chat into the page')
                        : _t('Pop-out the chat into a popup')
                    }
                    tooltipPlacement="bottom"
                    onClick={onPopupButtonClick}
                    disabled={loading}
                    data-testid={popupButtonMode === 'in' ? 'chat-header-pop-in' : 'chat-header-pop-out'}
                  />
                )}
                {onSearch && (
                  <IconButton
                    ref={searchButtonRef}
                    variant="ghostSecondary"
                    size="small"
                    icon={<SearchIcon />}
                    aria-label={_t('Find in chat')}
                    tooltip={_t('Find in chat')}
                    tooltipPlacement="bottom"
                    onClick={onSearch}
                    disabled={loading}
                    data-testid="chat-header-search"
                  />
                )}
                {onNewChat && (
                  <IconButton
                    variant="ghostSecondary"
                    size="small"
                    intent="newChat"
                    onClick={onNewChat}
                    disabled={loading}
                    data-testid="chat-header-new-chat"
                  />
                )}
                {onSettings && (
                  <IconButton
                    ref={settingsButtonRef}
                    variant="ghostSecondary"
                    size="small"
                    intent="setting"
                    onClick={onSettings}
                    disabled={loading}
                    data-testid="chat-header-settings"
                  />
                )}
                {onToggleFullScreen && (
                  <IconButton
                    variant="ghostSecondary"
                    size="small"
                    icon={isFullScreen ? <MinimizeIcon /> : <MaximizeIcon />}
                    aria-label={isFullScreen ? _t('Collapse') : _t('Expand')}
                    tooltip={isFullScreen ? _t('Collapse') : _t('Expand')}
                    tooltipPlacement="bottom"
                    onClick={onToggleFullScreen}
                    disabled={loading}
                    data-testid="chat-header-fullscreen"
                  />
                )}
                {onClose && (
                  <IconButton
                    variant="ghostSecondary"
                    size="small"
                    intent="close"
                    onClick={onClose}
                    data-testid="chat-header-close"
                  />
                )}
              </div>
            )}
          </div>
          {showFade && <div css={styles.fade} aria-hidden={true} data-testid="chat-header-fade" />}
        </>
      )}
    </header>
  );
};

export default ChatHeader;
